/**
 * Shared helpers to merge the global sidebar filters into a panel's query params. Each filter
 * is a list (0..N selected values). The backend reads them via
 * ``request.query_params.getlist(name)`` and normalizes empty strings + the "All" sentinel, so
 * we only need to drop empty arrays here.
 *
 * IMPORTANT: ``build-url`` joins array values with commas by default (``?sector=A,B``), which
 * Django's ``getlist`` would read as one element. ``buildRcaUrl`` passes ``disableCSV: true``
 * so arrays become repeated params (``?sector=A&sector=B``) — call it instead of ``buildUrl``
 * directly whenever filters might be present.
 */

import buildUrl from "build-url";

export interface RcaFilters {
    sector?: string[];
    industry?: string[];
    commodity?: string[];
    enterprise_type?: string[];
}

export function applyFilterParams(
    params: Record<string, any>,
    filters: RcaFilters | null | undefined,
): Record<string, any> {
    if (!filters) return params;
    const out = { ...params };
    if (filters.sector && filters.sector.length) out.sector = filters.sector;
    if (filters.industry && filters.industry.length)
        out.industry = filters.industry;
    if (filters.commodity && filters.commodity.length)
        out.commodity = filters.commodity;
    if (filters.enterprise_type && filters.enterprise_type.length) {
        out.enterprise_type = filters.enterprise_type;
    }
    return out;
}

/**
 * Build a URL for a road-closure-analytics endpoint, merging in any active sidebar filters and
 * forcing repeated-param serialization for array values. The signature mirrors a thin layer
 * over ``buildUrl`` so call sites stay compact.
 */
export function buildRcaUrl(
    url: string,
    params: Record<string, any>,
    filters?: RcaFilters | null,
): string {
    return buildUrl(url, {
        queryParams: applyFilterParams(params, filters),
        disableCSV: true,
    });
}
